import React, {
  ReactElement,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import { Text, Flex } from 'rebass/styled-components';

import useAccountsColumns from './useAccountsColumns';
import CopyAccount from '../CopyAccount';
import ImportAccount from '../ImportAccount';

import {
  MainContent,
  PageContent,
  ButtonGroup,
  PageHeader,
  Table,
} from 'ui/patterns';
import { Button } from 'ui/components/button';

import { useGetAccounts, useDocumentTitle, useGetUserSettings } from 'hooks';
import { TResult as ListItem } from 'hooks/useGetAccounts';

import * as Icon from '../../assets/icons';

import { Account } from 'types/accounts';

const pageTitle = 'List of Accounts';

const filterOptions = [
  { id: 0, filter: 'Live' },
  { id: 1, filter: 'Hidden' },
  { id: 2, filter: 'All' },
];

const sortItem = { sortKey: 'Phone Display', sortOrder: 'asc' };

export default function AccountsList(): ReactElement {
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [keywordSearch, setKeywordSearch] = useState(''); // keyword search
  const [accountStatus, setAccountStatus] = useState('Live'); // account filter
  // import and copy account
  const [showImportAccount, setImportAccount] = useState(false);
  const [showCopyAccount, setCopyAccount] = useState(false);
  const [accountDetails, setAccountDetails] = useState<Account>();

  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;
  const history = useHistory();
  useDocumentTitle('Index', true);

  const { data: userSettings } = useGetUserSettings();

  const {
    data: accountList,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAccounts(
    accountStatus,
    keywordSearch,
    columnsSearch,
    currentSortItem,
  );

  const columns = useAccountsColumns(userSettings);

  const pageActions = [
    {
      action: 'Reset',
      icon: <Icon.Refresh title="" ariaHidden="true" />,
      handleButtonClick: () => handleReset(),
    },
    {
      action: 'Add Account',
      icon: <Icon.Add title="" ariaHidden="true" />,
      handleButtonClick: () => handleAddAccount(),
    },
  ];

  // to preserve the filter values
  useEffect(() => {
    const savedaccountStatus = window.sessionStorage.getItem('accountStatus');
    const savedkeywordSearch = window.sessionStorage.getItem('keywordSearch');
    const savedcolumnsSearch = window.sessionStorage.getItem('columnsSearch');
    const savedcurrentSortItem =
      window.sessionStorage.getItem('currentSortItem');
    savedaccountStatus && setAccountStatus(savedaccountStatus);
    savedkeywordSearch && setKeywordSearch(savedkeywordSearch);
    savedcolumnsSearch && setColumnsSearch(JSON.parse(savedcolumnsSearch));
    savedcurrentSortItem &&
      setCurrentSortItem(JSON.parse(savedcurrentSortItem));
  }, []);

  useEffect(() => {
    window.sessionStorage.setItem('accountStatus', accountStatus);
    window.sessionStorage.setItem('keywordSearch', keywordSearch);
    window.sessionStorage.setItem(
      'columnsSearch',
      JSON.stringify(columnsSearch),
    );
    window.sessionStorage.setItem(
      'currentSortItem',
      JSON.stringify(currentSortItem),
    );
  }, [accountStatus, keywordSearch, columnsSearch, currentSortItem]);

  const accountData = useMemo(() => {
    if (accountDetails) {
      const { CID, SID, PID, StatusId, AccountServiceId, Name, AnswerScript } =
        accountDetails;
      return {
        CID,
        SID,
        PID,
        StatusId,
        AccountServiceId,
        Name,
        AnswerScript,
      };
    }
  }, [accountDetails]);

  const tableData = useMemo(() => {
    if (accountList) {
      return accountList?.pages
        ?.map((dataItem: ListItem) => dataItem.Data)
        .flat();
    }
  }, [accountList]);

  const totalItems = useMemo(() => {
    if (accountList) {
      return accountList.pages[0].FilteredRecords;
    }
    return 0;
  }, [accountList]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const toggleImportAccount = () => {
    setImportAccount(false);
    setAccountDetails(undefined);
  };

  const toggleCopyAccount = () => {
    setCopyAccount(false);
    setAccountDetails(undefined);
  };

  const setAccounts = (row: any, type: string) => {
    if (type === 'ImportAccount') {
      setImportAccount(true);
      setAccountDetails(row);
    }
    if (type === 'CopyAccount') {
      setCopyAccount(true);
      setAccountDetails(row);
    }
  };

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleClickFilter = (filterValue: string): void => {
    setAccountStatus(filterValue);
  };

  const handleReset = (): void => {
    setAccountStatus('Live');
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
  };

  const handleAddAccount = (): void => {
    history.push('/home/accounts/add');
  };

  const handleEditAccount = (row: any): void => {
    history.push(`/home/accounts/edit/${row.original.Id}/accountInfo`, {
      ID: row.original.Id,
    });
  };

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };

  const pageAddons = () => (
    <div className="addons">
      <ButtonGroup className="button-group">
        {filterOptions.map(filterValue => (
          <Button
            key={filterValue.id}
            variant={
              filterValue.filter === accountStatus ? 'primary' : undefined
            }
            onClick={() => handleClickFilter(filterValue.filter)}
          >
            {filterValue.filter}
          </Button>
        ))}
      </ButtonGroup>
    </div>
  );

  const toggleAdvancedSearch = () => {
    if (
      history.location.pathname === '/home/accounts' ||
      history.location.pathname === '/home/accounts/'
    ) {
      history.push('/home/accounts/advanced');
    } else {
      history.push('/home/accounts');
    }
  };

  return (
    <>
      <MainContent flexDirection="column">
        <PageHeader
          title={pageTitle}
          actions={pageActions}
          hasSearch
          addons={pageAddons()}
          searchValue={keywordSearch}
          handleChange={handleKeywordSearch}
        />
        <PageContent>
          <Flex justifyContent="space-between" alignItems="flex-end" mb={2}>
            <Text mb={2} fontSize={1} color="text.body" sx={{ height: '20px' }}>
              {tableData && tableData.length > 0 && (
                <Text mb={2}>{`Showing ${tableData.length} of ${totalItems}${
                  isFetching ? ' | Processing' : ''
                }`}</Text>
              )}
            </Text>
            <Tooltip
              animation="fade"
              position="left"
              arrow={true}
              arrowSize="small"
              html={
                <span>
                  Search procedures, sections and questions <br /> across the
                  selected accounts.
                </span>
              }
            >
              <Button
                variant="secondary"
                onClick={() => toggleAdvancedSearch()}
              >
                Advanced Search
              </Button>
            </Tooltip>
          </Flex>
          <Table
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            hasColumnSelection
            headerUpdateCallBack={headerUpdateCallBack}
            handleSortChange={handleSortChange}
            sortedItem={currentSortItem}
            columnsSearch={columnsSearch}
            userSettings={userSettings}
            onRowDoubleClick={handleEditAccount}
            setAccounts={setAccounts}
            onEndReached={onEndReached}
            isFetching={!!isFetchingNextPage}
          />
        </PageContent>
      </MainContent>
      {showImportAccount && (
        <ImportAccount
          showPanel={showImportAccount}
          togglePanel={toggleImportAccount}
          accountName={accountDetails?.AccountNameAndNumber || ''}
          importAccountId={accountDetails?.Id || 0}
          account={accountData ? accountData : undefined}
        />
      )}

      {showCopyAccount && (
        <CopyAccount
          showPanel={showCopyAccount}
          togglePanel={toggleCopyAccount}
          account={accountDetails}
        />
      )}
    </>
  );
}
